import { FC, useState, useEffect } from 'react';
import { Add, Close, Info, Save, EventAvailable, Delete, Edit, DragIndicator } from '@mui/icons-material';
import { Box, Button, Fade, Grid, Stack, Typography, ButtonGroup, useTheme, TextField } from '@mui/material';
import styled from '@emotion/styled';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Card, Loader, Modal } from '../../../components';
import { IPunchlistTemplate, IPunchlistTemplateItem } from '../../../models';
import { createPunchlistTemplate, getPunchlistTemplate, updatePunchlistTemplate } from '../../../fetch';

interface IPunchlistTemplateModal {
  open: boolean;
  onClose: () => void;
  currentPunchlistTemplate: IPunchlistTemplate;
  fetchPunchlistTemplates: () => void;
}

const EMPTY_PUNCHLIST_TEMPLATE = {
  punchlistTemplateId: undefined,
  name: '',
  description: '',
  numberOfToDoItems: 0,
  punchlistItems: []
}

export const PunchlistTemplateModal: FC<IPunchlistTemplateModal> = ({ open, onClose, currentPunchlistTemplate: editPunchlistTemplate, fetchPunchlistTemplates }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [currentPunchlistTemplate, setCurrentPunchlistTemplate] = useState<IPunchlistTemplate>(EMPTY_PUNCHLIST_TEMPLATE);
  const [initialPunchlistTemplate, setInitialPunchlistTemplate] = useState<IPunchlistTemplate>(EMPTY_PUNCHLIST_TEMPLATE);
  const [isLoadingPunchlistItems, setIsLoadingPunchlistItems] = useState(false);
  const [editingToDo, setEditingToDo] = useState({
    index: -1,
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newToDo, setNewToDo] = useState<string>('');

  useEffect(() => {
    setCurrentPunchlistTemplate({
      ...editPunchlistTemplate,
      punchlistItems: [],
    });

    const fetchPunchlistItems = async () => {
      try {
        setIsLoadingPunchlistItems(true);
        const response = await getPunchlistTemplate(editPunchlistTemplate.punchlistTemplateId);
        response.punchlistItems.sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1)
        setCurrentPunchlistTemplate(response);
        // Clone so it is not modified unexpectedly
        setInitialPunchlistTemplate({
          ...response,
          punchlistItems: [...response.punchlistItems],
        });
      } catch (err) {
        enqueueSnackbar(err?.Detail || 'Error loading punchlist items!', {
          variant: 'error'
        });
      } finally {
        setIsLoadingPunchlistItems(false);
      }
    }

    if (editPunchlistTemplate?.punchlistTemplateId) {
      fetchPunchlistItems();
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPunchlistTemplate, enqueueSnackbar])

  const handleClose = () => {
    if (!deepEqual(initialPunchlistTemplate, currentPunchlistTemplate) || Boolean(newToDo)) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        resetForm();
        setNewToDo('');
        onClose();
      } else {
        return;
      }
    } else {
      resetForm();
      setNewToDo('');
      onClose();
    }
  };

  const handleAddToDo = (values) => {
    const punchlistItems = currentPunchlistTemplate.punchlistItems;
    punchlistItems.push({
      description: newToDo,
      sortOrder: punchlistItems?.length ?? 0,
    });
    setCurrentPunchlistTemplate({
      ...currentPunchlistTemplate,
      punchlistItems,
    });
    setNewToDo('');
  }

  const handleDelete = (index) => {
    resetEditingToDo();
    setCurrentPunchlistTemplate({
      ...currentPunchlistTemplate,
      punchlistItems: currentPunchlistTemplate.punchlistItems.filter((item, idx) => idx !== index),
    })
  }

  const handleSaveItem = () => {
    const punchlistItems = currentPunchlistTemplate.punchlistItems;
    punchlistItems[editingToDo.index].description = editingToDo.description;
    setCurrentPunchlistTemplate({
      ...currentPunchlistTemplate,
      punchlistItems,
    });
    resetEditingToDo();
  }

  const handleCancelItem = () => {
    resetEditingToDo();
  }

  const resetForm = () => {
    setCurrentPunchlistTemplate({
      punchlistTemplateId: undefined,
      name: '',
      description: '',
      numberOfToDoItems: 0,
      punchlistItems: []
    });
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (newToDo) {
      window.alert('You have an unsaved to-do item');
      return;
    }

    try {
      setIsSubmitting(true);
      const isNew = !Boolean(currentPunchlistTemplate.punchlistTemplateId);
      const sorted = {
        ...currentPunchlistTemplate,
        punchlistItems: currentPunchlistTemplate.punchlistItems.map((item, index) => {
          return {
            punchlistTemplateId: isNew ? undefined : currentPunchlistTemplate.punchlistTemplateId,
            description: item.description,
            sortOrder: index,
          }
        })
      }
      const response = isNew ? await createPunchlistTemplate(sorted) : await updatePunchlistTemplate(currentPunchlistTemplate.punchlistTemplateId, sorted);
      if (response.Detail) {
        enqueueSnackbar(response.Detail, {
          variant: 'error'
        });
      } else {
        enqueueSnackbar(`Punchlist template ${isNew ? 'created' : 'updated'}!`, {
          variant: 'success'
        });
        fetchPunchlistTemplates();
        resetForm();
        setNewToDo('');
        onClose();
      }
    } catch (err) {
      enqueueSnackbar(err?.Detail || 'Error saving punchlist template!', {
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const punchlistItems = [...currentPunchlistTemplate.punchlistItems];
    const [removed] = punchlistItems.splice(result.source.index, 1);
    punchlistItems.splice(result.destination.index, 0, removed);

    setCurrentPunchlistTemplate({
      ...currentPunchlistTemplate,
      punchlistItems,
    })
  }

  const resetEditingToDo = () => {
    setEditingToDo({
      index: -1,
      description: '',
    });
  }

  const isEditingToDoItem = editingToDo.index >= 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      maxWidth="md"
    >
      {isSubmitting && <Loader type="overlay" position="centered" />}
      <Fade in={open}>
        <form onSubmit={handleSubmit} autoComplete="none">
          <Box marginBottom="1rem">
            <Typography variant="h5">{currentPunchlistTemplate?.punchlistTemplateId ? `Edit Punchlist Template` : 'Add Punchlist Template'}</Typography>
          </Box>
          <Card icon={<Info />} title="Info">
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6}>
                <TextField
                  required
                  size="small"
                  fullWidth
                  label="Name"
                  variant="outlined"
                  name="name"
                  value={currentPunchlistTemplate.name}
                  onChange={(event) => setCurrentPunchlistTemplate({
                    ...currentPunchlistTemplate,
                    name: event.target.value,
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  size="small"
                  fullWidth
                  label="Description"
                  variant="outlined"
                  name="description"
                  value={currentPunchlistTemplate.description}
                  onChange={(event) => setCurrentPunchlistTemplate({
                    ...currentPunchlistTemplate,
                    description: event.target.value,
                  })}
                />
              </Grid>
            </Grid>
          </Card>
          <Card icon={<EventAvailable />} title="To-Do Items">
            {isLoadingPunchlistItems && <Loader type="overlay" position="centered" />}
            {currentPunchlistTemplate.punchlistItems.length > 0 && (
              <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
                  <Droppable droppableId="todo-droppable" direction="vertical">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {currentPunchlistTemplate.punchlistItems.map((toDoItem: IPunchlistTemplateItem, index, items) => (
                          <Draggable key={index} draggableId={`draggable-todo-${index}`} index={index} isDragDisabled={isEditingToDoItem}>
                            {(provided, snapshot) => (
                              <Row
                                ref={provided.innerRef}
                                isDragging={snapshot.isDragging}
                                dragColor={theme.palette.grey[300]}
                                hoverColor={isEditingToDoItem ? 'inherit' : theme.palette.grey[100]}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Stack key={index} flexDirection="row" gap={1} spacing={1} alignItems="center">
                                  <DragIndicator color={isEditingToDoItem ? 'disabled' : 'action'} />
                                  {editingToDo.index === index ?
                                    <>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        required
                                        label="Description"
                                        style={{ marginTop: '8px', marginBottom: '8px' }}
                                        value={editingToDo.description}
                                        onChange={(event) => setEditingToDo({
                                          ...editingToDo,
                                          description: event.target.value,
                                        })}
                                      />
                                      <Box style={{ marginTop: 0 }}>
                                        <Button
                                          size="small"
                                          color="primary"
                                          startIcon={<Save />}
                                          onClick={handleSaveItem}
                                          style={{ marginTop: 0 }}
                                        >
                                          Save
                                        </Button>
                                      </Box>
                                      <Box style={{ marginTop: 0 }}>
                                        <Button
                                          size="small"
                                          color="error"
                                          startIcon={<Close />}
                                          onClick={handleCancelItem}
                                          style={{ marginTop: 0 }}
                                        >
                                          Cancel
                                        </Button>
                                      </Box>
                                    </> :
                                    <>
                                      <Box style={{ marginTop: 0, flex: 1, paddingLeft: 5 }}>
                                        <Typography sx={{ marginTop: '5px', marginBottom: '5px' }}>{toDoItem.description}</Typography>
                                      </Box>
                                      <Box style={{ marginTop: 0 }}>
                                        <Button
                                          size="small"
                                          color="primary"
                                          startIcon={<Edit />}
                                          onClick={() => setEditingToDo({
                                            index,
                                            description: toDoItem.description,
                                          })}
                                          disabled={isEditingToDoItem}
                                          style={{ marginTop: 0 }}
                                        >
                                          Edit
                                        </Button>
                                      </Box>
                                      <Box style={{ marginTop: 0 }}>
                                        <Button
                                          size="small"
                                          color="error"
                                          startIcon={<Delete />}
                                          onClick={() => handleDelete(index)}
                                          disabled={isEditingToDoItem}
                                          style={{ marginTop: 0 }}
                                        >
                                          Delete
                                        </Button>
                                      </Box>
                                    </>
                                  }
                                </Stack>
                              </Row>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
              </DragDropContext>
            )}
            <Box mt={1}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                name="newToDo"
                label="Description"
                value={newToDo}
                onChange={(event) => setNewToDo(event.target.value)}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" my={1}>
              <ButtonGroup>
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddToDo}
                  disabled={!Boolean(newToDo)}
                >
                  Add To-do
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Close />}
                  color="inherit"
                  onClick={() => setNewToDo('')}
                  disabled={!Boolean(newToDo)}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Box>
          </Card>
          <Box marginTop="2rem">
            <Button disabled={isSubmitting || !Boolean(currentPunchlistTemplate.name && currentPunchlistTemplate.description)} type="submit" startIcon={<Save />} variant="contained" color="primary">
              Save
            </Button>
            <Button
              type="button"
              variant="contained"
              color="inherit"
              startIcon={<Close />}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Fade>
    </Modal>
  );
};

interface IRowProps {
  isDragging: boolean;
  dragColor: string;
  hoverColor: string;
}

const Row = styled.div`
  background-color: ${(props: IRowProps) => props.isDragging && props.dragColor};
  &:hover {
    background-color: ${(props: IRowProps) => props.hoverColor};
  }
`;
